import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { Cookies } from "react-cookie";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import PaletteIcon from "@mui/icons-material/Palette";
import CheckIcon from "@mui/icons-material/Check";
import { ColorModeContext } from "../App";
import { paletteVariants, PaletteKey } from "../theme";

const Navbar = () => {
  const [fullName, setFullName] = useState("");
  const [paletteAnchor, setPaletteAnchor] = useState<HTMLButtonElement | null>(
    null
  );
  const theme = useTheme();
  const { toggleColorMode, paletteKey, setPaletteKey } =
    useContext(ColorModeContext);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/userapi/check-auth/`,
          { withCredentials: true }
        );
        if (response.status === 200) setFullName(response.data.full_name);
      } catch (error) {
        console.error("Error checking auth status", error);
      }
    };
    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      const csrfToken = new Cookies().get("csrftoken");
      console.log("csrfToken", csrfToken);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/userapi/logout/`,
        null,
        { withCredentials: true, headers: { "X-CSRFTOKEN": csrfToken } }
      );
      if (response.status === 200) setFullName("");
    } catch (error) {
      console.error("Error logging out", error);
    }
  };

  return (
    <AppBar position="sticky" color="primary">
      <Toolbar>
        <Typography
          component={RouterLink}
          to="/"
          variant="h6"
          sx={{
            fontFamily: theme.fonts.display,
            color: "inherit",
            textDecoration: "none",
            flexGrow: 1,
          }}
        >
          Tournament Home
        </Typography>

        {/* Dark/light toggle */}
        <IconButton
          onClick={toggleColorMode}
          color="inherit"
          title={
            theme.palette.mode === "dark"
              ? "Switch to light mode"
              : "Switch to dark mode"
          }
        >
          {theme.palette.mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* Palette picker */}
        <IconButton
          onClick={(e) => setPaletteAnchor(e.currentTarget)}
          color="inherit"
          title="Choose color palette"
          sx={{ mr: 1 }}
        >
          <PaletteIcon />
        </IconButton>

        <Popover
          open={Boolean(paletteAnchor)}
          anchorEl={paletteAnchor}
          onClose={() => setPaletteAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { mt: 1 } } }}
        >
          <Box sx={{ p: 2, minWidth: 230 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1.5, color: "text.secondary" }}
            >
              Color Theme
            </Typography>
            {(Object.entries(paletteVariants) as [PaletteKey, (typeof paletteVariants)[PaletteKey]][]).map(
              ([key, config]) => (
                <Box
                  key={key}
                  onClick={() => {
                    setPaletteKey(key);
                    setPaletteAnchor(null);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    bgcolor:
                      paletteKey === key ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0.25,
                  }}
                >
                  {/* Swatch: primary circle + secondary dot */}
                  <Box sx={{ position: "relative", width: 30, height: 30, flexShrink: 0 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        bgcolor: config.swatchPrimary,
                        border: "2px solid",
                        borderColor: "divider",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -2,
                        right: -2,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: config.swatchSecondary,
                        border: "2px solid",
                        borderColor: "background.paper",
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {config.label}
                  </Typography>
                  {paletteKey === key && (
                    <CheckIcon
                      sx={{ fontSize: 16, color: "primary.main" }}
                    />
                  )}
                </Box>
              )
            )}
          </Box>
        </Popover>

        {!fullName && (
          <Button
            component={RouterLink}
            to="/login"
            color="inherit"
            variant="outlined"
            sx={{ borderColor: "rgba(255,255,255,0.5)" }}
          >
            Login or Sign Up
          </Button>
        )}
        {fullName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1">Welcome, {fullName}</Typography>
            <Button
              onClick={handleLogout}
              color="secondary"
              variant="contained"
              size="small"
            >
              Logout
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
